import React, { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import FileManagerModal from '../components/FileManagerModal';

interface FileManagerContextType {
  openFileManager: (onSelect: (url: string) => void) => void;
  closeFileManager: () => void;
  isOpen: boolean;
}

const FileManagerContext = createContext<FileManagerContextType | undefined>(undefined);

export const FileManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [onSelectCallback, setOnSelectCallback] = useState<((url: string) => void) | null>(null);
  // Incremented each time openFileManager is called — used to detect stale close timeouts
  const openVersionRef = useRef(0);

  const openFileManager = useCallback((onSelect: (url: string) => void) => {
    openVersionRef.current += 1;
    setOnSelectCallback(() => onSelect);
    setIsOpen(true);
  }, []);

  const closeFileManager = useCallback(() => {
    setIsOpen(false);
    // Capture the version at close time. Only clear the callback if no new openFileManager
    // call happened in the meantime — prevents a rapid re-open from losing its callback.
    const versionAtClose = openVersionRef.current;
    setTimeout(() => {
      if (openVersionRef.current === versionAtClose) {
        setOnSelectCallback(null);
      }
    }, 300);
  }, []);

  const handleSelect = useCallback((url: string) => {
    if (onSelectCallback) {
      onSelectCallback(url);
    }
    // La fermeture est gérée par le composant FileManagerModal qui appelle onClose après onSelect
    // Mais par sécurité, on peut aussi s'assurer que l'état est mis à jour
    // setIsOpen(false); // Laissons le modal gérer la fermeture via la prop onClose
  }, [onSelectCallback]);

  // Expose a global helper for TinyMCE buttons (CloudFileManager)
  useEffect(() => {
    const opener = (options: { onFileSelect: (file: { url: string }) => void }) => {
      if (!options?.onFileSelect) return;
      openFileManager((url) => options.onFileSelect({ url }));
    };
    (window as any).__FH_OPEN_FILE_MANAGER = opener;
    return () => {
      if ((window as any).__FH_OPEN_FILE_MANAGER === opener) {
        delete (window as any).__FH_OPEN_FILE_MANAGER;
      }
    };
  }, [openFileManager]);

  return (
    <FileManagerContext.Provider value={{ openFileManager, closeFileManager, isOpen }}>
      {children}
      {/* Le modal est rendu ici, au niveau racine du provider */}
      <FileManagerModal
        isOpen={isOpen}
        onClose={closeFileManager}
        onSelect={handleSelect}
      />
    </FileManagerContext.Provider>
  );
};

export const useFileManagerContext = () => {
  const context = useContext(FileManagerContext);
  if (context === undefined) {
    throw new Error('useFileManagerContext must be used within a FileManagerProvider');
  }
  return context;
};
